// Flash Report Data Entry Form Component
// This form collects raw data only - no calculations
// All calculations and analysis will be on the Report page
// NOTE: For Seattle, Rochester, and Abilene - Clean LBS and Soil LBS
//       are populated automatically by SQL Agent jobs and are read-only here
//       For all other plants - both fields are manually entered

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { flashReportAPI } from '../utils/api';
import { formatDateForInput, getMostRecentSaturday, isSaturday } from '../utils/dateHelpers';
import '../styles/FlashReportForm.css';

// Plant IDs that have automated LBS data from SQL Agent jobs
// These plants get Clean LBS and Soil LBS automatically - no manual entry needed
const AUTOMATED_LBS_PLANTS = [1, 4, 5, 7, 8]; // Seattle, St. Paul, Rochester, Abilene, San Antonio

function FlashReportForm() {
    const { user } = useAuth();
    
    // State for form inputs
    const [plants, setPlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState('');
    const [weekEnding, setWeekEnding] = useState('');
    
    // State for form data - only raw input fields
    // soil_lbs added for manual entry on non-automated plants
    const [formData, setFormData] = useState({
        pounds:         '',
        soil_lbs:       '',   // --- ADDED: manual soil LBS for non-automated plants ---
        revenue:        '',
        direct_hours:   '',
        direct_labor:   '',
        total_hours:    '',
        total_labor:    '',
        total_ot_hours: '',
        packroom_fte:   '',
        linen_dist_fte: ''
    });

    // State for automated LBS display (read-only, populated by SQL Agent)
    const [automatedLBS, setAutomatedLBS] = useState({
        clean_lbs: null,
        soil_lbs:  null
    });

    // State for previous week's data (for reference display)
    const [previousData, setPreviousData] = useState(null);
    const [previousWeekDate, setPreviousWeekDate] = useState('');

    // State for UI feedback
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load plants list when component mounts
    useEffect(() => {
        loadPlants();
        setWeekEnding(formatDateForInput(getMostRecentSaturday()));
    }, []);

    // Load previous week's data when week ending changes (for reference only)
    useEffect(() => {
        if (selectedPlant && weekEnding) {
            loadPreviousWeekData();
        }
    }, [selectedPlant, weekEnding]);

    // Clear form data when plant or date changes
    useEffect(() => {
        setFormData({
            pounds:         '',
            soil_lbs:       '',
            revenue:        '',
            direct_hours:   '',
            direct_labor:   '',
            total_hours:    '',
            total_labor:    '',
            total_ot_hours: '',
            packroom_fte:   '',
            linen_dist_fte: ''
        });
        // Reset automated LBS display when plant/date changes
        setAutomatedLBS({ clean_lbs: null, soil_lbs: null });
        setMessage({ text: '', type: '' });
    }, [selectedPlant, weekEnding]);

    // Check if the currently selected plant is one of the automated LBS plants
    const isAutomatedPlant = () => {
        return AUTOMATED_LBS_PLANTS.includes(parseInt(selectedPlant));
    };

    // Load plants from API
    const loadPlants = async () => {
        try {
            const data = await flashReportAPI.getPlants();
            
            let availablePlants = data.plants;
            
            if (!user.isAdmin) {
                const userPlantIds = user.permissions.map(p => p.plant_id);
                availablePlants = data.plants.filter(p => userPlantIds.includes(p.plant_id));
            }
            
            setPlants(availablePlants);
            
            // Select default plant if user has one, otherwise select first available
            if (availablePlants.length > 0) {
                if (user.default_plant_id) {
                    const hasAccessToDefault = availablePlants.some(p => p.plant_id === user.default_plant_id);
                    if (hasAccessToDefault) {
                        setSelectedPlant(user.default_plant_id.toString());
                    } else {
                        setSelectedPlant(availablePlants[0].plant_id.toString());
                    }
                } else {
                    setSelectedPlant(availablePlants[0].plant_id.toString());
                }
            }
        } catch (error) {
            setMessage({ text: 'Error loading plants', type: 'error' });
        }
    };

    // Load previous week's data for reference display
    const loadPreviousWeekData = async () => {
        try {
            const [year, month, day] = weekEnding.split('-').map(num => parseInt(num));
            const selectedDate = new Date(year, month - 1, day);
            const previousSaturday = new Date(selectedDate);
            previousSaturday.setDate(previousSaturday.getDate() - 7);
            
            const prevWeekStr = formatDateForInput(previousSaturday);
            
            console.log('🔍 Loading previous week data for:', prevWeekStr);
            setPreviousWeekDate(prevWeekStr);
            
            const data = await flashReportAPI.getReport(selectedPlant, prevWeekStr);
            
            if (data.report) {
                console.log('✅ Previous week data found:', data.report);
                setPreviousData(data.report);
            } else {
                console.log('ℹ️ No previous week data');
                setPreviousData(null);
            }
        } catch (error) {
            console.log('ℹ️ Could not load previous week data');
            setPreviousData(null);
        }
    };

    // Check if user has edit permission for selected plant
    const hasEditPermission = () => {
        if (user.isAdmin) return true;
        
        const permission = user.permissions.find(
            p => p.plant_id === parseInt(selectedPlant)
        );
        
        return permission && permission.permission_level === 'edit';
    };

    // Handle week ending date change with Saturday validation
    const handleWeekEndingChange = (e) => {
        const selectedDate = e.target.value;
        
        if (!isSaturday(selectedDate)) {
            setMessage({ 
                text: 'Week ending must be a Saturday. Please select a Saturday.', 
                type: 'error' 
            });
            return;
        }
        
        setWeekEnding(selectedDate);
        setMessage({ text: '', type: '' });
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Load existing data when "Load Existing" button is clicked
    const handleLoadData = async () => {
        if (!selectedPlant || !weekEnding) {
            setMessage({ text: 'Please select a plant and week ending date', type: 'error' });
            return;
        }

        try {
            setLoading(true);
            const data = await flashReportAPI.getReport(selectedPlant, weekEnding);
            console.log('🔍 API Response:', data);
            
            if (data.report) {
                setFormData({
                    pounds:         data.report.pounds         || '',
                    soil_lbs:       data.report.soil_lbs       || '',  // load soil_lbs for manual plants
                    revenue:        data.report.revenue        || '',
                    direct_hours:   data.report.direct_hours   || '',
                    direct_labor:   data.report.direct_labor   || '',
                    total_hours:    data.report.total_hours    || '',
                    total_labor:    data.report.total_labor    || '',
                    total_ot_hours: data.report.total_ot_hours || '',
                    packroom_fte:   data.report.packroom_fte   || '',
                    linen_dist_fte: data.report.linen_dist_fte || ''
                });

                // Load automated LBS values for display if this is an automated plant
                if (isAutomatedPlant()) {
                    setAutomatedLBS({
                        clean_lbs: data.report.clean_lbs,
                        soil_lbs:  data.report.soil_lbs
                    });
                }

                setMessage({ text: 'Data loaded successfully', type: 'success' });
            } else {
                setMessage({ text: 'No existing data - ready for new entry', type: 'info' });
            }
        } catch (error) {
            console.error('❌ Error loading data:', error);
            setMessage({ text: 'Error loading data', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedPlant || !weekEnding) {
            setMessage({ text: 'Please select a plant and week ending date', type: 'error' });
            return;
        }

        if (!hasEditPermission()) {
            setMessage({ text: 'You do not have edit permission for this plant', type: 'error' });
            return;
        }

        if (!isSaturday(weekEnding)) {
            setMessage({ text: 'Week ending must be a Saturday', type: 'error' });
            return;
        }

        try {
            setLoading(true);
            
            const dataToSave = {
                plant_id:       parseInt(selectedPlant),
                week_ending:    weekEnding,
                pounds:         formData.pounds         === '' ? 0 : parseFloat(formData.pounds),
                // ⚠️ Hidden — uncomment to restore Revenue in data entry
                // revenue:        formData.revenue        === '' ? 0 : parseFloat(formData.revenue),
                direct_hours:   formData.direct_hours   === '' ? 0 : parseFloat(formData.direct_hours),
                direct_labor:   formData.direct_labor   === '' ? 0 : parseFloat(formData.direct_labor),
                total_hours:    formData.total_hours    === '' ? 0 : parseFloat(formData.total_hours),
                total_labor:    formData.total_labor    === '' ? 0 : parseFloat(formData.total_labor),
                total_ot_hours: formData.total_ot_hours === '' ? 0 : parseFloat(formData.total_ot_hours),
                packroom_fte:   formData.packroom_fte   === '' ? 0 : parseInt(formData.packroom_fte),
                linen_dist_fte: formData.linen_dist_fte === '' ? 0 : parseInt(formData.linen_dist_fte),
                // For automated plants, don't send soil_lbs - SQL Agent manages it
                // For manual plants, send whatever was entered (null if empty)
                soil_lbs: isAutomatedPlant()
                    ? undefined
                    : (formData.soil_lbs === '' ? null : parseFloat(formData.soil_lbs))
            };

            const result = await flashReportAPI.createReport(dataToSave);
            console.log('✅ Save successful:', result);
            
            setMessage({ text: result.message, type: 'success' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('❌ Error saving data:', error);
            const errorMsg = error.response?.data?.message || 'Error saving data';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Clear form
    const handleClear = () => {
        setFormData({
            pounds:         '',
            soil_lbs:       '',
            revenue:        '',
            direct_hours:   '',
            direct_labor:   '',
            total_hours:    '',
            total_labor:    '',
            total_ot_hours: '',
            packroom_fte:   '',
            linen_dist_fte: ''
        });
        setAutomatedLBS({ clean_lbs: null, soil_lbs: null });
        setMessage({ text: '', type: '' });
    };

    // Format number for previous data display
    const formatPrevious = (value) => {
        if (!value) return '-';
        return parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
    };

    // Format automated LBS value for display
    // Shows the number if available, or a pending message if not yet populated by SQL Agent
    const formatAutomatedLBS = (value) => {
        if (value === null || value === undefined) return 'Pending automation...';
        return parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
    };

    const canEdit = hasEditPermission();
    const selectedPlantName = plants.find(p => p.plant_id.toString() === selectedPlant)?.plant_name || '';
    const automated = isAutomatedPlant();

    return (
        <div className="flash-report-form-container">
            <h2>Flash Report - Data Entry</h2>
            <p className="subtitle">Enter raw data only. Week ending must be a Saturday.</p>

            {!canEdit && selectedPlant && (
                <div className="permission-warning">
                    <strong>View Only Access:</strong> You have read-only access to {selectedPlantName}. 
                    You cannot enter or modify data.
                </div>
            )}

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="selection-section">
                <div className="form-group">
                    <label htmlFor="plant">Plant *</label>
                    <select
                        id="plant"
                        value={selectedPlant}
                        onChange={(e) => setSelectedPlant(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">Select a plant...</option>
                        {plants.map(plant => (
                            <option key={plant.plant_id} value={plant.plant_id}>
                                {plant.plant_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="weekEnding">Week Ending (Saturday) *</label>
                    <input
                        type="date"
                        id="weekEnding"
                        value={weekEnding}
                        onChange={handleWeekEndingChange}
                        disabled={loading}
                    />
                </div>

                <button 
                    type="button" 
                    onClick={handleLoadData}
                    className="btn-secondary"
                    disabled={loading || !selectedPlant || !weekEnding}
                >
                    See Current
                </button>
            </div>

            <form onSubmit={handleSubmit} className="flash-report-form">
                
                {/* Production Data — only shown for non-automated plants */}
                {/* Automated plants (Seattle, St. Paul, Rochester, Abilene) have no manual fields here */}
                {!automated && (
                <div className="form-section">
                    <h3>Production Data</h3>
                    <div className="form-row">

                        {/* Clean LBS */}
                        {/* TODO: Remove this entire commented block after 2026-08-17 - automated plants (Seattle/Rochester/Abilene) 
                            no longer need this section as SQL Agent jobs handle Clean LBS automatically */}
                        {/* {automated ? (
                            <div className="form-group">
                                <div className="label-row">
                                    <label htmlFor="pounds">
                                        Clean LBS
                                        <span className="automated-badge">⚙️ Automated</span>
                                    </label>
                                    {previousData && previousData.clean_lbs !== null && previousData.clean_lbs !== undefined && (
                                        <span className="previous-badge">
                                            Previous: {formatPrevious(previousData.clean_lbs)}
                                        </span>
                                    )}
                                </div>
                                <div className="automated-field">
                                    {formatAutomatedLBS(automatedLBS.clean_lbs)}
                                </div>
                            </div>
                        ) : ( */}
                        {!automated && (
                            <div className="form-group">
                                <div className="label-row">
                                    <label htmlFor="pounds">Clean LBS</label>
                                    {previousData && previousData.clean_lbs !== null && previousData.clean_lbs !== undefined && (
                                        <span className="previous-badge">
                                            Previous: {formatPrevious(previousData.clean_lbs)}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="number"
                                    id="pounds"
                                    name="pounds"
                                    value={formData.pounds}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    placeholder="0.00"
                                    disabled={loading || !canEdit}
                                    readOnly={!canEdit}
                                />
                            </div>
                        )}
                        {/* )} */}

                        {/* Soil LBS */}
                        {/* TODO: Remove this entire commented block after 2026-08-17 - automated plants (Seattle/Rochester/Abilene)
                            no longer need this section as SQL Agent jobs handle Soil LBS automatically */}
                        {/* {automated ? (
                            <div className="form-group">
                                <div className="label-row">
                                    <label htmlFor="soil_lbs">
                                        Soil LBS
                                        <span className="automated-badge">⚙️ Automated</span>
                                    </label>
                                    {previousData && previousData.soil_lbs !== null && previousData.soil_lbs !== undefined && (
                                        <span className="previous-badge">
                                            Previous: {formatPrevious(previousData.soil_lbs)}
                                        </span>
                                    )}
                                </div>
                                <div className="automated-field">
                                    {formatAutomatedLBS(automatedLBS.soil_lbs)}
                                </div>
                            </div>
                        ) : ( */}
                        {!automated && (
                            <div className="form-group">
                                <div className="label-row">
                                    <label htmlFor="soil_lbs">Soil LBS</label>
                                    {previousData && previousData.soil_lbs !== null && previousData.soil_lbs !== undefined && (
                                        <span className="previous-badge">
                                            Previous: {formatPrevious(previousData.soil_lbs)}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="number"
                                    id="soil_lbs"
                                    name="soil_lbs"
                                    value={formData.soil_lbs}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    placeholder="0.00"
                                    disabled={loading || !canEdit}
                                    readOnly={!canEdit}
                                />
                            </div>
                        )}
                        {/* )} */}

                        {/* ⚠️ Hidden — uncomment to restore Revenue in data entry */}
                        {/* <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="revenue">Revenue ($)</label>
                                {previousData && previousData.revenue !== null && previousData.revenue !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.revenue)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="revenue"
                                name="revenue"
                                value={formData.revenue}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div> */}

                    </div>
                </div>
                )} {/* end !automated — Production Data */}

                {/* Labor Data - unchanged */}
                <div className="form-section">
                    <h3>Labor Data</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="direct_hours">Direct Hours</label>
                                {previousData && previousData.direct_hours !== null && previousData.direct_hours !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.direct_hours)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="direct_hours"
                                name="direct_hours"
                                value={formData.direct_hours}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="direct_labor">Direct Labor ($)</label>
                                {previousData && previousData.direct_labor !== null && previousData.direct_labor !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.direct_labor)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="direct_labor"
                                name="direct_labor"
                                value={formData.direct_labor}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="total_hours">Total Hours</label>
                                {previousData && previousData.total_hours !== null && previousData.total_hours !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.total_hours)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="total_hours"
                                name="total_hours"
                                value={formData.total_hours}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="total_labor">Total Labor ($)</label>
                                {previousData && previousData.total_labor !== null && previousData.total_labor !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.total_labor)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="total_labor"
                                name="total_labor"
                                value={formData.total_labor}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="total_ot_hours">OT Hours</label>
                                {previousData && previousData.total_ot_hours !== null && previousData.total_ot_hours !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.total_ot_hours)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="total_ot_hours"
                                name="total_ot_hours"
                                value={formData.total_ot_hours}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>
                    </div>
                </div>

                {/* Staffing Data - unchanged */}
                <div className="form-section">
                    <h3>Staffing Data</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="packroom_fte">Packroom FTE</label>
                                {previousData && previousData.packroom_fte !== null && previousData.packroom_fte !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.packroom_fte)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="packroom_fte"
                                name="packroom_fte"
                                value={formData.packroom_fte}
                                onChange={handleInputChange}
                                step="1"
                                placeholder="0"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="linen_dist_fte">Linen Distr. FTE</label>
                                {previousData && previousData.linen_dist_fte !== null && previousData.linen_dist_fte !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.linen_dist_fte)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="linen_dist_fte"
                                name="linen_dist_fte"
                                value={formData.linen_dist_fte}
                                onChange={handleInputChange}
                                step="1"
                                placeholder="0"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>
                    </div>
                </div>

                {canEdit && (
                    <div className="form-actions">
                        <button 
                            type="button"
                            onClick={handleClear}
                            className="btn-clear"
                            disabled={loading}
                        >
                            Clear Form
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary"
                            disabled={loading || !selectedPlant || !weekEnding}
                        >
                            {loading ? 'Saving...' : 'Save Data'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}

export default FlashReportForm;